import React, { useMemo, Fragment } from "react";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import { useFormikContext } from "formik";
import FieldListFactory from "components/FieldListFactory";
import { STATE_KEYS } from "utils/constants/state";
import { useAppCheckout } from "utils/selectors";
import { LIST_PICKER_CUSTOM_CLASSES } from "utils/constants/ui";
import OptionListPicker from "components/OptionListPicker";
import { useIsManifestEditMode } from "utils/hooks/manifest";
import LegalEditorField from "components/LegalEditorField";
import debounce from "lodash/debounce";
import { DEFAULT_DEBOUNCE } from "utils/constants/form";
import classnames from "classnames";
import { getUUID } from "utils/uuid";
import { usePageBuilderContext } from "utils/context";

const ValuesFields = ({ name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const isEditMode = useIsManifestEditMode();
  const builderCtx = usePageBuilderContext();
  const setComponentRefreshKey =
    builderCtx && builderCtx.setComponentRefreshKey;

  const checkout = useAppCheckout();

  const inlineContentName = `${name}.${STATE_KEYS.LEGAL.CONTENT_INLINE_CONTENT}`;
  const inlineContent = get(values, inlineContentName);
  const inlineContentTouched = get(touched, inlineContentName);
  /**
   * On update:
   * - update values content
   * - "broadcast" changes to other components by rolling the componentRefreshKey
   */
  const onUpdate = ({ editor }) => {
    setFieldValue(inlineContentName, editor.getJSON());

    setTimeout(() => {
      setComponentRefreshKey && setComponentRefreshKey(getUUID());
      if (!inlineContentTouched) {
        setFieldTouched(inlineContentName, true);
      }
    }, 0);
  };
  const debounceInlineUpdate = useMemo(
    () => debounce(onUpdate, DEFAULT_DEBOUNCE),
    []
  );
  const ctxInlineContent = inlineContent || {
    type: "doc",
    content: [
      {
        type: "paragraph"
      }
    ]
  };
  const inlineContentEditable = !isEditMode;
  const acceptanceTypeOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.LEGAL_ACCEPTANCE_TYPE_OPTIONS) || [];
  const acceptanceTypePath = getFieldPath(
    name,
    STATE_KEYS.LEGAL.ACCEPTANCE_TYPE
  );

  const acceptanceTypeValue = get(values, acceptanceTypePath);

  const acceptanceTypeProps = {
    label: "Acceptance type",
    value: acceptanceTypeValue,
    name: acceptanceTypePath,
    multiselect: false,
    disabled: isEditMode,
    options: acceptanceTypeOptions,
    onClick: (_, selectedValue) => {
      setFieldValue(acceptanceTypePath, selectedValue);
      if (!get(touched, acceptanceTypePath)) {
        setFieldTouched(name, true);
      }
    },
    canUnset: false,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  const presentationTypeOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.LEGAL_PRESENTATION_TYPE_OPTIONS) ||
    [];
  const presentationTypePath = getFieldPath(
    name,
    STATE_KEYS.LEGAL.PRESENTATION_TYPE
  );
  const presentationTypeValue = get(values, presentationTypePath);

  const presentationTypeProps = {
    label: "Presentation type",
    value: presentationTypeValue,
    name: presentationTypePath,
    multiselect: false,
    disabled: isEditMode,
    options: presentationTypeOptions,
    onClick: (_, selectedValue) => {
      setFieldValue(presentationTypePath, selectedValue);
      if (!get(touched, presentationTypePath)) {
        setFieldTouched(name, true);
      }
    },
    canUnset: false,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  return (
    <Fragment>
      <FieldListFactory
        name={name}
        config={[
          {
            name: "label"
          },
          {
            name: "description"
          }
        ]}
      />
      <div className="pt3">
        <label className="f6 mb2 dib fw6">Content</label>
        <div
          className={classnames("input--common__simulated br-6", {
            disabled: !inlineContentEditable
          })}
        >
          <LegalEditorField
            key={inlineContentEditable ? "edit" : "locked"}
            editable={inlineContentEditable}
            name={inlineContentName}
            content={ctxInlineContent}
            onUpdate={debounceInlineUpdate}
          />
        </div>
      </div>
      <div className="w-100 pt3 mt3 bt hairline-1">
        <div className="f5 pb2 fw6">Acceptance type</div>
        <div className="f6 lh-copy">
          How the customer acknowledges and accepts legal terms.
        </div>
        <div className="pt3">
          <OptionListPicker {...acceptanceTypeProps} />
        </div>
      </div>
      <div className="w-100 pt3 dn">
        <div className="f5 pb2 fw6">Presentation</div>
        <div className="f6 lh-copy">How to present the legal content.</div>
        <div className="pt3">
          <OptionListPicker {...presentationTypeProps} />
        </div>
      </div>
    </Fragment>
  );
};

export default ValuesFields;
